/// <amd-module name="Test/TestResultWorkItems/Actions/WorkItemsActionCreator" />
import { IViewContextData } from 'Test/common-util/Common';
import { getProjectName } from 'Test/common-util/Common.Utils';
import { TestCaseResult } from 'Test/common-util/Contracts/Test';
import * as Telemetry from 'Test/common-util/Telemetry';
import { IWorkItemWithColorsAndIcon, WorkItemsActionsHub } from 'Test/TestResultWorkItems/Actions/WorkItemsActionHub';
import { WorkItemTypes } from 'Test/TestResultWorkItems/Common';
import * as Resources from 'Test/TestResultWorkItems/Resources';
import { WorkItemsGridViewSource } from 'Test/TestResultWorkItems/Source/WorkItemsGridViewSource';
import * as TelemetryConstants from 'Test/TestResultWorkItems/TelemetryConstants';
import { ITfsPageService } from "Tfs/Platform/Page";
import { announce } from 'VSS/Core/Util/Accessibility';
import * as Utils_String from 'VSS/Core/Util/String';
import { IVssPageContext } from 'VSS/Platform/Context';
import { IVssContributionService } from 'VSS/Platform/Contribution';
import { routeUrl } from 'VSS/Platform/Location';
import { WorkItem, WorkItemStateColor } from 'Wit/Clients/Wit/Contracts/WorkItemTracking';
import { WorkItemTypeColorAndIcon, WorkItemTypeColorAndIcons } from 'WorkItemsHub/External/NewWorkItem/WorkItemTypeColorAndIconsProvider';

export class WorkItemsActionsCreator {

    constructor(private _pageContext: IVssPageContext, private _viewContext: IViewContextData, private _actionsHub: WorkItemsActionsHub, private _source: WorkItemsGridViewSource) {
    }

    public async loadAssociatedWorkItems(testCaseResult: TestCaseResult): Promise<void> {
        let workItems: [WorkItem[], WorkItem[]] | void = await Promise.all([
            this._source.loadAssociatedBugs(testCaseResult, this._pageContext),
            this._source.loadAssociatedRequirements(testCaseResult, this._pageContext)
        ]).catch((error) => {
            this._actionsHub.onError.invoke(error.message);
        });

        if (workItems) {
            workItems = this._makeArraysExclusive(workItems);
            let contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            // To be changed if the data provider changes
            const colorIconDataProviderName = "ms.vss-work-web.work-item-type-color-icon-data-provider";
            const stateIconDataProviderName = "ms.vss-work-web.work-item-states-color-data-provider";
            let colorIconDataProvider = await contributionService.getDataAsync(colorIconDataProviderName);
            let stateIconDataProvider = await contributionService.getDataAsync(stateIconDataProviderName);
            let workItemTypeToStateColorMap = contributionService.getData<{ [key: string]: WorkItemStateColor[] }>(stateIconDataProviderName);
            let projectToIconAndColorMap = contributionService.getData<WorkItemTypeColorAndIcon[]>(colorIconDataProviderName);
            projectToIconAndColorMap?.forEach(colorAndIcon => {
                colorAndIcon.color = WorkItemTypeColorAndIcons.transformColor(colorAndIcon.color);
            });
            if (!workItemTypeToStateColorMap || !projectToIconAndColorMap) {
                Telemetry.publishEvent(this._pageContext, this._viewContext, TelemetryConstants.featureWorkItemsPivotGridView_StateColorAndIconNotLoaded, {});
            }
            workItems[0].sort(this._workItemIdComparer);
            workItems[1].sort(this._workItemIdComparer);
            this._actionsHub.workItemsLoaded.invoke({
                "bugs": workItems[0],
                "requirements": workItems[1],
                "stateAndColorMap": workItemTypeToStateColorMap,
                "colorAndIconMap": projectToIconAndColorMap
            });
        }
    }

    private _workItemIdComparer(item1: WorkItem, item2: WorkItem) {
        if (item1.id > item2.id) {
            return -1; // because we need to sort in descending order
        }
        return 1;
    }

    private _makeArraysExclusive(workItems: [WorkItem[], WorkItem[]]): [WorkItem[], WorkItem[]] {
        let bugs = workItems[0];
        let requirements = workItems[1];
        if (bugs.length === 0 || requirements.length === 0) {
            return workItems;
        }
        // Store bugs array in a map
        let bugsMap: { [key: number]: boolean } = {};
        bugs.forEach((bug) => {
            bugsMap[bug.id] = true;
        });
        // Create a new array and store the requirements that were not present in bugs by checking in the hashmap
        let newRequirements: WorkItem[] = [];
        requirements.forEach((requirement) => {
            if (!bugsMap[requirement.id]) {
                newRequirements.push(requirement);
            }
        });
        workItems[0] = bugs;
        workItems[1] = newRequirements;
        return workItems;
    }

    public openWorkItem(workItem: WorkItem): string {
        const url = this.makeURLforWI(workItem.id);
        return url;
    }

    public makeURLforWI(itemId: number): string {
        const openWIRouteId = "ms.vss-work-web.work-items-form-route-with-id";
        const tfsPageService = this._pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        let projectName: string = "";

        if (tfsPageData && tfsPageData.project) {
            projectName = tfsPageData.project.name;
        }

        return (projectName && projectName != "")
            ? routeUrl(this._pageContext, openWIRouteId, {
                project: projectName,
                id: `${itemId}`
            })
            : "";
    }

    public async updateBug(workItem: WorkItem): Promise<void> {
        try {
            let updatedWorkItem: WorkItem = await this._source.getUpdatedBugs(workItem, this._pageContext);
            this._updateWorkItem(updatedWorkItem, true);
        }
        catch (error) {
            this._handleError(error);
        }
    }

    public async updateRequirement(workItem: WorkItem): Promise<void> {
        try {
            let updatedWorkItem: WorkItem = await this._source.getUpdatedRequirements(workItem, this._pageContext);
            this._updateWorkItem(updatedWorkItem, false);
        }
        catch (error) {
            this._handleError(error);
        }
    }

    private _updateWorkItem(updatedWorkItem: WorkItem, isBugUpdated: boolean) {
        if (isBugUpdated) {
            this._actionsHub.updateWorkItems.invoke({ "updatedWorkItem": updatedWorkItem, "workItemType": WorkItemTypes.Bug });
        }
        else {
            this._actionsHub.updateWorkItems.invoke({ "updatedWorkItem": updatedWorkItem, "workItemType": WorkItemTypes.Requirements });
        }
    }

    public async deleteBugs(testCaseResult: TestCaseResult, workItems: IWorkItemWithColorsAndIcon[]): Promise<void> {
        try {
            let unlinkedWorkItems: WorkItem[] = await this._source.deleteAssociationsOfBugs(testCaseResult, workItems, this._pageContext);
            this._actionsHub.onDeleteWorkItemLink.invoke({ "workItems": unlinkedWorkItems, "workItemType": WorkItemTypes.Bug });
            let bugs: WorkItem[] = [];
            workItems.forEach((bug) => {
                bugs.push(bug.workItem);
            });
            this.deleteAssociations(bugs, unlinkedWorkItems);
        }
        catch (error) {
            this._handleError(error);
        }
    }

    public async deleteRequirements(testCaseResult: TestCaseResult, workItems: IWorkItemWithColorsAndIcon[]): Promise<void> {
        try {
            let unlinkedWorkItems: WorkItem[] = await this._source.deleteAssociationsOfRequirements(testCaseResult, workItems, this._pageContext);
            this._actionsHub.onDeleteWorkItemLink.invoke({ "workItems": unlinkedWorkItems, "workItemType": WorkItemTypes.Requirements });
            let requirements: WorkItem[] = [];
            workItems.forEach((requirement) => {
                requirements.push(requirement.workItem);
            });
            this.deleteAssociations(requirements, unlinkedWorkItems);
        }
        catch (error) {
            this._handleError(error);
        }
    }

    public deleteAssociations(workItems: WorkItem[], unlinkedWorkItems: WorkItem[]): void {
        if (unlinkedWorkItems.length > 0) {
            announce(Utils_String.format(Resources.AnnounceAssociationsDeleted, unlinkedWorkItems.length));
        }
        if (unlinkedWorkItems.length !== workItems.length) {
            let remainingWorkItems: WorkItem[] = workItems.filter((x) => { return unlinkedWorkItems.indexOf(x) < 0; });
            let errorMessage: string = Utils_String.format(Resources.UnableToDeleteAssociationText, remainingWorkItems.map(x => x.fields["System.Title"]).join(Resources.CommaSeparator));
            this._handleCustomError(errorMessage);
        }
    }

    public afterSortBugs(workItems: IWorkItemWithColorsAndIcon[]): void {
        this._actionsHub.onSort.invoke({ "workItems": workItems, "workItemType": WorkItemTypes.Bug });
    }

    public afterSortRequirements(workItems: IWorkItemWithColorsAndIcon[]): void {
        this._actionsHub.onSort.invoke({ "workItems": workItems, "workItemType": WorkItemTypes.Requirements });
    }

    public clearState(): void {
        this._actionsHub.clearState.invoke(null);
    }

    private _handleError(error: Error) {
        this._actionsHub.onError.invoke(error.message || error.toString());
    }

    private _handleCustomError(error: string) {
        this._actionsHub.onError.invoke(error);
    }

    public sortItems(items: IWorkItemWithColorsAndIcon[], sortBy: string, descending = false): IWorkItemWithColorsAndIcon[] {
        let valueToReturn: number = 1;
        if (!descending) {
            valueToReturn = -1;
        }
        return items.sort((item1: IWorkItemWithColorsAndIcon, item2: IWorkItemWithColorsAndIcon) => {
            if (sortBy === "id") {
                if (item1.workItem[sortBy] < item2.workItem[sortBy]) {
                    return valueToReturn;
                }
                if (item1.workItem[sortBy] > item2.workItem[sortBy]) {
                    return -1 * valueToReturn;
                }
                return 0;
            } else if (sortBy === "System.AssignedTo") {
                if (!item1.workItem.fields[sortBy]) {
                    return valueToReturn;
                }
                if (!item2.workItem.fields[sortBy]) {
                    return -1 * valueToReturn;
                }
                if (item1.workItem.fields[sortBy].toString() < item2.workItem.fields[sortBy].toString()) {
                    return valueToReturn;
                }
                if (item1.workItem.fields[sortBy].toString() > item2.workItem.fields[sortBy].toString()) {
                    return -1 * valueToReturn;
                }
                return 0;
            } else {
                if (item1.workItem.fields[sortBy] < item2.workItem.fields[sortBy]) {
                    return valueToReturn;
                }
                if (item1.workItem.fields[sortBy] > item2.workItem.fields[sortBy]) {
                    return -1 * valueToReturn;
                }
                return 0;
            }
        });
    }
}
